import React, { useEffect, memo } from "react";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables, ChartDataLabels);

const StackedBarChart = ({
  type,
  data,
  labels,
  legend,
  titl,
  XAxisLabel,
  YAxisLabel,
  id,
  chartType,
  FormalReview,
  countOnLead,
  barDataLoaded,
  maxVal,
  minVal,
  dollar,
  ChartHeight = 400,
  ValueFontSize = 8,
}) => {
  if ((!data || data.length == 0) && !countOnLead) {
    return <div className="Nodata">No data available</div>;
  } else if ((!data || data.length == 0) && countOnLead) {
    return <div className="NodataLeads">No data available</div>;
  }

  const title = "Year - " + titl;
  const colors = [
    "#ebcaae",
    "#f68a78",
    "#f3e573",
    "#ebb556",
    "#79d4c5",
    "#a7e7db",
    "#a7cae7",
    "#50d790",
    "#caee9c",
    "#d1d65d",
    "#fbd9bb",
    "#e3b286",
    "#d17b75",
    "#e39c92",
    "#ffbc8b",
    "#8ed9b7",
    "#ffc58f",
    "#7cc3d9",
    "#f4c2c2",
    "#f5e0b7",
    "#b0d6e4",
    "#d6e9c6",
    "#eac4d5",
  ];

  const dataset = {
    labels: labels,
    datasets: legend?.map((dataset, index) => ({
      label: legend[index],
      data: data?.map((v) => v[index]),
      backgroundColor: colors[index],
      borderWidth: 1,
      barPercentage:
        data.length === 1
          ? 0.1
          : data.length === 2
          ? 0.2
          : data.length === 3
          ? 0.3
          : data.length === 4
          ? 0.3
          : data.length === 5
          ? 0.4
          : data.length === 6
          ? 0.5
          : data.length === 7
          ? 0.6
          : data.length === 8
          ? 0.6
          : 0.9,
    })),
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1.9,
    plugins: {
      title: {
        display: false,
        text: title,
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "x",
        },
      },
      legend: {
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            family: "optimare",
            size: countOnLead ? 12 : 10,
            style: "normal",
            weight: "bold",
            lineHeight: 1.2,
          },
        },
        onHover: (event, item, legend) => {
          legend.chart.data.datasets[item.datasetIndex].pointStyle = "circle";
          legend.chart.update();
        },
      },
      datalabels: {
        display: true,
        color: "grey",
        anchor: "center",
        align: "center",
        font: {
          size: ValueFontSize,
          family: "optimare",
        },

        formatter: function (value) {
          return value == 0
            ? ""
            : dollar
            ? `$${(value / 1000).toFixed(1)}K`
            : value;
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 52,
        grid: {
          display: false,
          drawBorder: false,
          drawOnChartArea: true,
          drawTicks: true,
        },
        stacked: true,
        title: {
          display: true,
          text: XAxisLabel,
          color: "#9d7a4d",
          font: {
            family: "optimare",
            weight: "bold",
            lineHeight: 1.2,
            size: 12,
          },
          padding: { top: 0, left: 0, right: 0, bottom: 10 },
        },
        ticks: {
          font: {
            family: "optimare",
            weight: "bold",
          },
        },
        min: minVal,
        max: maxVal,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          display: false,
          drawBorder: false,
          drawOnChartArea: true,
          drawTicks: true,
        },
        ticks: {
          font: {
            family: "optimare",
            weight: "bold",
          },
          callback: function (v) {
            if (dollar) {
              return `$${(v / 1000).toFixed(1)}K`;
            } else {
              return v;
            }
          },
        },
        title: {
          display: true,
          text: YAxisLabel,
          color: "#9d7a4d",
          font: {
            family: "optimare",
            weight: "bold",
            lineHeight: 1.2,
            size: 12,
          },
          padding: { top: 0, left: 0, right: 0, bottom: 10 },
        },
      },
    },

    barPercentage: 0.5,
    categoryPercentage: 0.8,
  };

  return (
    <div className="chart-container" style={{ height: ChartHeight }}>
      <Bar data={dataset} options={options} id={id} />
    </div>
  );
};

export default memo(StackedBarChart);
